import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { getSession, logout, getStatsRecords } from "../lib/auth/client";

interface StatsRecord {
  id: string;
  textContent: string;
  statsJson: string;
  createdAt: string;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [records, setRecords] = useState<StatsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const session = getSession();
    if (!session) {
      navigate({ to: "/login" });
      return;
    }
    setUserName(session.name || "User");
    setRecords(getStatsRecords());
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    logout();
    navigate({ to: "/char-count" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">{t("common.processing", "Processing...")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            {t("dashboard.title", "Dashboard")}
          </h1>
          <p className="text-gray-600">
            {t("dashboard.welcome", "Welcome")}, {userName}!
          </p>
        </div>
        <Button variant="outline" onClick={handleLogout}>
          {t("auth.logout", "Logout")}
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl">
            📊 {t("dashboard.history", "Statistics History")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {t("dashboard.noRecords", "No saved records yet. Use the character counter and save your results!")}
            </p>
          ) : (
            <div className="space-y-3">
              {records.map((record) => {
                const stats = JSON.parse(record.statsJson);
                return (
                  <div
                    key={record.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm text-gray-500">
                        {new Date(record.createdAt).toLocaleString()}
                      </span>
                      <div className="flex gap-4 text-sm">
                        <span className="text-blue-600 font-medium">
                          {stats.total || 0} {t("charCount.total", "chars")}
                        </span>
                        <span className="text-green-600 font-medium">
                          {stats.words || 0} {t("charCount.words", "words")}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm truncate">
                      {record.textContent.slice(0, 100)}
                      {record.textContent.length > 100 ? "..." : ""}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-center">
        <Button onClick={() => navigate({ to: "/char-count" })}>
          {t("dashboard.useTool", "Use Character Counter")}
        </Button>
      </div>
    </div>
  );
}
